import { useState, type ReactNode } from "react";
import { notifications as notifApi } from "../api/client";
import { useAsync, usePolling } from "../hooks/useAsync";
import { Link, NavLink, useNavigate } from "react-router";
import { useSession } from "../auth/session";
import { initials } from "../lib/format";
import { t } from "../i18n";
import { Icon, Wordmark } from "./ui";
import { SandboxPanel } from "./SandboxPanel";

interface NavItem {
  to: string;
  label: string;
  short?: string;
  icon: () => ReactNode;
  end?: boolean;
}

const CLIENT_NAV: NavItem[] = [
  { to: "/", label: t.nav.painel, icon: Icon.home, end: true },
  { to: "/pagamentos", label: t.nav.pagamentos, icon: Icon.arrow },
  { to: "/fornecedores", label: t.nav.fornecedores, icon: Icon.people },
  { to: "/documentos", label: t.nav.documentos, icon: Icon.doc },
  { to: "/configuracoes", label: t.nav.configuracoes, icon: Icon.gear },
];

const TOCA_NAV: NavItem[] = [
  { to: "/toca", label: t.nav.tocaFila, icon: Icon.flag, end: true },
  { to: "/toca/pedidos", label: t.nav.tocaPedidos, icon: Icon.arrow },
  { to: "/toca/acessos", label: t.nav.tocaAcessos, icon: Icon.people },
  { to: "/toca/kyb", label: t.nav.tocaOnboarding, icon: Icon.people },
  { to: "/toca/fornecedores", label: t.nav.tocaFornecedores, icon: Icon.people },
  { to: "/toca/reconciliacao", label: t.nav.tocaReconciliacao, short: t.nav.tocaReconciliacaoShort, icon: Icon.doc },
  { to: "/toca/configuracoes", label: t.nav.tocaConfiguracoes, icon: Icon.gear },
  { to: "/toca/auditoria", label: t.nav.tocaAuditoria, icon: Icon.list },
];

export interface PageProps {
  title: string;
  crumb?: string;
  actions?: ReactNode;
  /** link "voltar" no mobile */
  backTo?: string;
  children: ReactNode;
}

function Sidebar({ items, toca }: { items: NavItem[]; toca: boolean }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const staff = me?.user.staffRole;
  const name = toca ? `${me?.user.name?.split(" ")[0] ?? ""} · ${staff ?? ""}` : me?.company?.tradeName || me?.company?.legalName || me?.user.name || "";
  const sub = toca ? me?.user.email ?? "" : `${me?.user.name?.split(" ")[0] ?? ""}${me?.role ? " · " + t.roles[me.role] : ""}`;
  return (
    <aside className="sidebar">
      <div className="brand">
        <Link to={toca ? "/toca" : "/"} style={{ textDecoration: "none" }} aria-label={t.brand.name}>
          <Wordmark />
        </Link>
        {toca && <span className="badge-toca">{t.toca.badge}</span>}
      </div>
      <nav className="nav" aria-label={toca ? t.nav.toca : t.nav.painel}>
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => (isActive ? "on" : "")}>
            {it.icon()}
            <span>{it.label}</span>
          </NavLink>
        ))}
        {staff && !toca && (
          <NavLink to="/toca" className="">
            {Icon.flag()}
            <span>{t.nav.goToToca}</span>
          </NavLink>
        )}
        {toca && me?.company && (
          <NavLink to="/" end className="">
            {Icon.home()}
            <span>{t.nav.goToPanel}</span>
          </NavLink>
        )}
      </nav>
      <button type="button" className="who" onClick={() => navigate(toca ? "/toca/configuracoes" : "/configuracoes")} style={{ border: 0, cursor: "pointer", textAlign: "left" }}>
        <div className={`av ${toca ? "staff" : ""}`}>{initials(toca ? me?.user.name : name)}</div>
        <div className="txt">
          <b>{name}</b>
          <span>{sub}</span>
        </div>
      </button>
      <LogoutButton className="logout-side" />
    </aside>
  );
}

/** Sino com contagem de não lidas; atualiza a cada 60 s e ao voltar para a aba. Só para empresa aprovada. */
function NotificationsBell() {
  const { me } = useSession();
  const enabled = Boolean(me?.company && me.company.status === "approved");
  const { data, reload } = useAsync(() => (enabled ? notifApi.list() : Promise.resolve([])), [enabled]);
  usePolling(() => reload(), 60_000, enabled);
  if (!enabled) return null;
  const unread = (data ?? []).filter((n) => !n.readAt).length;
  return (
    <Link to="/notificacoes" className="bell" aria-label={unread ? `${t.notifications.bell}: ${unread}` : t.notifications.bell}>
      {Icon.bell()}
      {unread > 0 && <span className="badge">{unread > 99 ? "99+" : unread}</span>}
    </Link>
  );
}

/** "Sair": encerra a sessão no servidor e volta para o login. Aparece na barra lateral, no topo no mobile e no cadastro. */
export function LogoutButton({ className = "" }: { className?: string }) {
  const { logout } = useSession();
  const navigate = useNavigate();
  const [out, setOut] = useState(false);
  async function doLogout() {
    setOut(true);
    try {
      await logout();
    } finally {
      navigate("/entrar", { replace: true });
    }
  }
  return (
    <button type="button" className={`btn ghost ${className}`} onClick={doLogout} disabled={out} aria-label={t.auth.logout}>
      {Icon.logout()}
      <span>{out ? t.settings.loggingOut : t.auth.logout}</span>
    </button>
  );
}

function TabBar({ items }: { items: NavItem[] }) {
  const shown = items.slice(0, 5);
  return (
    <nav className="tabbar" aria-label="Navegação">
      {shown.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => (isActive ? "on" : "")}>
          {it.icon()}
          <span>{it.short ?? it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Frame({ items, toca, title, crumb, actions, backTo, children }: PageProps & { items: NavItem[]; toca: boolean }) {
  return (
    <div className="app">
      <Sidebar items={items} toca={toca} />
      <main className="main">
        <header className="topbar">
          <div className="ttl">
            {backTo && (
              <Link to={backTo} className="backlink" aria-label={t.common.back}>
                {Icon.back()}
              </Link>
            )}
            <h1>{title}</h1>
            {crumb && <span className="crumb">{crumb}</span>}
          </div>
          <div className="act">
            {actions}
            {!toca && <NotificationsBell />}
            <LogoutButton className="logout-top" />
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
      <TabBar items={items} />
      {!toca && <SandboxPanel />}
    </div>
  );
}

export function Page(props: PageProps) {
  return <Frame items={CLIENT_NAV} toca={false} {...props} />;
}

export function TocaPage(props: PageProps) {
  return <Frame items={TOCA_NAV} toca {...props} />;
}

/** Layout sem navegação (onboarding). */
export function BarePage({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="app">
      <main className="main">
        <header className="topbar">
          <div className="ttl">
            <Wordmark style={{ fontSize: 20, paddingBottom: 8 }} />
            <h1 style={{ marginLeft: 8 }}>{title}</h1>
          </div>
          <div className="act">
            {right}
            <LogoutButton />
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
